import { useState, useEffect } from "react";
import API from "../api";
import {
  Edit3,
  Save,
  Trash2,
  AlertTriangle,
} from "lucide-react";

import { useAuth } from "../context/AuthContext";
import { useParams, useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";

function OrganizationSettings() {

  const [org, setOrg] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loadingPage, setLoadingPage] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const { user, loading } = useAuth();

  const { orgSlug } = useParams();

  const navigate = useNavigate();

  // =========================
  // FETCH ORG
  // =========================
  const fetchOrg = async () => {

    try {

      const res = await API.get(
        `get-organization/?slug=${orgSlug}`
      );

      const data = res.data.organization;

      setOrg({
        basic: data?.basic || {},
        contact: data?.contact || {},
        legal: data?.legal || {},
        address: data?.address || {},
      });

    } catch (err) {

      console.error("Fetch Org Error:", err);

    } finally {

      setLoadingPage(false);

    }
  };

  // =========================
  // LOAD
  // =========================
  useEffect(() => {

    if (loading) return;
    if (!user) return;
    if (!orgSlug) return;

    fetchOrg();

  }, [loading, user, orgSlug]);

  // =========================
  // SAVE
  // =========================
  const handleSave = async () => {

    try {

      setSaving(true);

      await API.put(
        "update-organization/",
        org
      );

      alert("Organization updated successfully");

      setEditMode(false);

    } catch (err) {

      console.error(err);

      alert("Update failed");

    } finally {

      setSaving(false);

    }
  };

  // =========================
  // DELETE ORGANIZATION
  // =========================
  const handleDeleteOrganization = async () => {

  const confirmDelete = window.confirm(
    "Are you sure you want to permanently delete this organization?"
  );

  if (!confirmDelete) return;

  try {

    setDeleting(true);

    // 🔥 DELETE ORG
    await API.delete(
  `/delete-organization/?slug=${orgSlug}`
);

    // 🔥 CLEAR LOCAL STORAGE
    localStorage.removeItem("orgId");
    localStorage.removeItem("orgSlug");
    localStorage.removeItem("role");
    localStorage.removeItem("creatingOrg");

    // 🔥 FIREBASE LOGOUT
    await signOut(auth);

    alert("Organization deleted successfully");

    // 🔥 HARD REDIRECT
    window.location.href = "/login";

  } catch (err) {

    console.error(err);

    // 🔥 IF ORG ALREADY DELETED
    if (
      err.response?.status === 403 ||
      err.response?.status === 404
    ) {

      localStorage.clear();

      await signOut(auth);

      window.location.href = "/login";

      return;
    }

    alert(
      err.response?.data?.error ||
      "Failed to delete organization"
    );

  } finally {

    setDeleting(false);

  }
};

  // =========================
  // LOADING
  // =========================
  if (loading || loadingPage || !org) {
    return (
      <div className="p-10 text-center text-slate-500">
        Loading organization...
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">

      {/* HEADER */}
      <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8 mb-8">

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">

          <div>
            <h1 className="text-3xl font-black text-slate-800">
              Organization Settings
            </h1>

            <p className="text-slate-500 mt-1">
              Manage organization profile & business details
            </p>
          </div>

          <div className="flex gap-3">

            {!editMode ? (
              <button
                onClick={() => setEditMode(true)}
                className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-3 rounded-2xl font-bold"
              >
                <Edit3 size={16} />
                Edit
              </button>
            ) : (
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-3 rounded-2xl font-bold"
              >
                <Save size={16} />
                {saving ? "Saving..." : "Save"}
              </button>
            )}

          </div>
        </div>
      </div>

      {/* BASIC */}
      <MainCard>
        <Section title="Basic Details">

          <Field
            label="Company Name"
            value={org.basic.companyName}
            editMode={editMode}
            onChange={(v)=>
              setOrg({
                ...org,
                basic:{
                  ...org.basic,
                  companyName:v
                }
              })
            }
          />

          <Field
            label="Company Size"
            value={org.basic.companySize}
            editMode={editMode}
            onChange={(v)=>
              setOrg({
                ...org,
                basic:{
                  ...org.basic,
                  companySize:v
                }
              })
            }
          />

          <Field
            label="Business Type"
            value={org.basic.businessType}
            editMode={editMode}
            onChange={(v)=>
              setOrg({
                ...org,
                basic:{
                  ...org.basic,
                  businessType:v
                }
              })
            }
          />

        </Section>
      </MainCard>

      {/* CONTACT */}
      <MainCard>
        <Section title="Contact Details">

          <Field
            label="Official Email"
            value={org.contact.officialEmail}
            editMode={editMode}
            onChange={(v)=>
              setOrg({
                ...org,
                contact:{
                  ...org.contact,
                  officialEmail:v
                }
              })
            }
          />

          <Field
            label="Phone Number"
            value={org.contact.phone}
            editMode={editMode}
            onChange={(v)=>
              setOrg({
                ...org,
                contact:{
                  ...org.contact,
                  phone:v
                }
              })
            }
          />

        </Section>
      </MainCard>

      {/* LEGAL */}
      <MainCard>
        <Section title="Legal Details">

          <Field
            label="GST Number"
            value={org.legal.gstNumber}
            editMode={editMode}
            onChange={(v)=>
              setOrg({
                ...org,
                legal:{
                  ...org.legal,
                  gstNumber:v
                }
              })
            }
          />

          <Field
            label="PAN Number"
            value={org.legal.panNumber}
            editMode={editMode}
            onChange={(v)=>
              setOrg({
                ...org,
                legal:{
                  ...org.legal,
                  panNumber:v
                }
              })
            }
          />

          <Field
            label="TAN Number"
            value={org.legal.tanNumber}
            editMode={editMode}
            onChange={(v)=>
              setOrg({
                ...org,
                legal:{
                  ...org.legal,
                  tanNumber:v
                }
              })
            }
          />

          <Field
            label="CIN Number"
            value={org.legal.cinNumber}
            editMode={editMode}
            onChange={(v)=>
              setOrg({
                ...org,
                legal:{
                  ...org.legal,
                  cinNumber:v
                }
              })
            }
          />

        </Section>
      </MainCard>

      {/* ADDRESS */}
      <MainCard>
        <Section title="Address">

          <Field
            label="Country"
            value={org.address.country}
            editMode={editMode}
            onChange={(v)=>
              setOrg({
                ...org,
                address:{
                  ...org.address,
                  country:v
                }
              })
            }
          />

          <Field
            label="State"
            value={org.address.state}
            editMode={editMode}
            onChange={(v)=>
              setOrg({
                ...org,
                address:{
                  ...org.address,
                  state:v
                }
              })
            }
          />

          <Field
            label="District"
            value={org.address.district}
            editMode={editMode}
            onChange={(v)=>
              setOrg({
                ...org,
                address:{
                  ...org.address,
                  district:v
                }
              })
            }
          />

          <Field
            label="City"
            value={org.address.city}
            editMode={editMode}
            onChange={(v)=>
              setOrg({
                ...org,
                address:{
                  ...org.address,
                  city:v
                }
              })
            }
          />

          <Field
            label="Pincode"
            value={org.address.pincode}
            editMode={editMode}
            onChange={(v)=>
              setOrg({
                ...org,
                address:{
                  ...org.address,
                  pincode:v
                }
              })
            }
          />

          <Field
            label="Full Address"
            value={org.address.fullAddress}
            editMode={editMode}
            onChange={(v)=>
              setOrg({
                ...org,
                address:{
                  ...org.address,
                  fullAddress:v
                }
              })
            }
          />

        </Section>
      </MainCard>

      {/* DANGER ZONE */}
      <div className="bg-red-50 border border-red-200 rounded-3xl p-8 mt-10">

        <div className="flex items-start gap-4">

          <div className="w-14 h-14 rounded-2xl bg-red-100 flex items-center justify-center">
            <AlertTriangle className="text-red-600" />
          </div>

          <div className="flex-1">

            <h2 className="text-xl font-black text-red-700">
              Danger Zone
            </h2>

            <p className="text-red-500 mt-1">
              Permanently delete this organization,
              employees, clients, and all related data.
            </p>

            <button
              onClick={handleDeleteOrganization}
              disabled={deleting}
              className="mt-5 flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-5 py-3 rounded-2xl font-bold"
            >
              <Trash2 size={16} />

              {deleting
                ? "Deleting..."
                : "Delete Organization"}
            </button>

          </div>
        </div>
      </div>

    </div>
  );
}

/* ========================= */
/* MAIN CARD */
/* ========================= */

const MainCard = ({ children }) => (
  <div className="bg-white border border-slate-200 rounded-3xl shadow-sm p-8 mb-8">
    {children}
  </div>
);

/* ========================= */
/* SECTION */
/* ========================= */

const Section = ({ title, children }) => (
  <div>
    <h3 className="text-xl font-black text-slate-800 mb-6">
      {title}
    </h3>

    <div className="grid md:grid-cols-2 gap-6">
      {children}
    </div>
  </div>
);

/* ========================= */
/* FIELD */
/* ========================= */

const Field = ({
  label,
  value,
  editMode,
  onChange,
}) => (
  <div>

    <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">
      {label}
    </p>

    {editMode ? (
      <input
        value={value || ""}
        onChange={(e)=>onChange(e.target.value)}
        className="w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-indigo-500"
      />
    ) : (
      <div className="bg-slate-50 rounded-2xl px-4 py-3 font-semibold text-slate-700">
        {value || "-"}
      </div>
    )}

  </div>
);

export default OrganizationSettings;